import type { EmulatorRow } from "@app/emulators";
import type { Session } from "@app/session";
import { openDeploy, openFeedback, setUi, useUi } from "@app/ui";
import { availability, useFeature } from "@config/features";
import { getSnapshotDisplayName, getSnapshotSecondary } from "@repo/components/ctrl/snapshots";
import { useSnapshots, useTenantMembers, useTenantTools } from "@repo/components/ctrl/hooks";
import { Modal, useMantineColorScheme } from "@repo/ui/core";
import { useHotkeys } from "@repo/ui/hooks";
import {
  IconArrowRight,
  IconBrandGitlab,
  IconBuildingPlus,
  IconCode,
  IconDevices,
  IconKey,
  IconTerminal2,
  IconMessage2,
  IconMoon,
  IconRocket,
  IconSearch,
  IconStack2,
  IconTool,
  IconSun,
  IconUser,
  IconUserPlus,
  type Icon,
} from "@repo/ui/icons";
import { Kbd } from "@repo/ui/na";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toolPath, kindLabel } from "@components/access/toolsFormat";
import type { NavSection } from "../nav";
import classes from "./shell.module.scss";

type Item = { id: string; icon: Icon; label: string; sub: string; run: () => void; text?: string };

type Props = {
  sections: NavSection[];
  emulators: EmulatorRow[];
  session: Session;
};

/** ⌘K: emulators, snapshots, members, flows and navigation. Enter runs the selection. */
export function CommandPalette({ sections, emulators, session }: Props) {
  const { palette } = useUi();
  const close = () => setUi({ palette: false });
  useHotkeys([["mod+K", () => setUi({ palette: !palette })]], [], true);

  return (
    <Modal
      opened={palette}
      onClose={close}
      withCloseButton={false}
      size={600}
      yOffset="12vh"
      centered={false}
      padding={0}
      aria-label="Command palette"
    >
      {palette && <PaletteBody sections={sections} emulators={emulators} session={session} close={close} />}
    </Modal>
  );
}

function PaletteBody({ sections, emulators, session, close }: Props & { close: () => void }) {
  const navigate = useNavigate();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const { data: snaps } = useSnapshots({ revalidateOnFocus: false });
  const { data: members } = useTenantMembers(session.isAdmin ? session.tenantId : undefined);

  const go = (to: string) => () => {
    close();
    navigate(to);
  };

  const toolDistribution = useFeature("toolDistribution");
  const { data: tenantTools } = useTenantTools(
    toolDistribution ? session.tenantId : undefined,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const items = useMemo<Item[]>(() => {
    const wsOn = availability("workspaces") === "available";
    const flows: Item[] = [
      { id: "f-deploy", icon: IconRocket, label: "Deploy emulator", sub: "Action", run: () => openDeploy() },
      ...(wsOn ? [{ id: "f-ws", icon: IconCode, label: "Create workspace", sub: "Action", run: go("/workspaces?new=1") }] : []),
      { id: "f-link", icon: IconBrandGitlab, label: "Link GitLab", sub: "Flow", run: go("/link/gitlab") },
      ...(session.isAdmin
        ? [
            { id: "f-member", icon: IconUserPlus, label: "Add member", sub: "Flow", run: go("/org/members?add=1") },
            { id: "f-access", icon: IconKey, label: "Manage resource access", sub: "Flow", run: go("/org/access?mode=resource") },
          ]
        : []),
      ...(session.isGlobalAdmin
        ? [{ id: "f-onboard", icon: IconBuildingPlus, label: "Onboard tenant", sub: "Flow", run: go("/platform/tenants/new") }]
        : []),
      {
        id: "f-theme",
        icon: colorScheme === "dark" ? IconSun : IconMoon,
        label: colorScheme === "dark" ? "Switch to light theme" : "Switch to dark theme",
        sub: "Action",
        run: () => {
          toggleColorScheme();
          close();
        },
      },
      { id: "f-cli", icon: IconTerminal2, label: "Set up nullctl", sub: "Help", run: go("/cli") },
      { id: "f-fb", icon: IconMessage2, label: "Send feedback", sub: "Help", run: openFeedback },
    ];
    const emus: Item[] = emulators.map((e) => ({
      id: `e-${e.id}`,
      icon: IconDevices,
      label: e.name,
      sub: `Emulator · ${e.status}`,
      text: `${e.name} ${e.id} ${e.ownerName} ${e.project ?? ""}`,
      run: go(`/emulators/${e.id}`),
    }));
    const snapItems: Item[] = (snaps ?? []).map((s) => ({
      id: `s-${s.name}`,
      icon: IconStack2,
      label: getSnapshotDisplayName(s),
      sub: `Snapshot · ${getSnapshotSecondary(s) ?? ""}`,
      text: `${getSnapshotDisplayName(s)} ${s.name} ${s.job_sha ?? ""}`,
      run: go(`/builds?snapshot=${encodeURIComponent(s.name ?? "")}`),
    }));
    const people: Item[] = (members ?? []).map((m) => ({
      id: `m-${m.id}`,
      icon: IconUser,
      label: m.name ?? m.email ?? "",
      sub: "Member",
      text: `${m.name} ${m.email}`,
      run: go(`/org/access?user=${m.id}`),
    }));
    const toolItems: Item[] = (tenantTools ?? []).map((t) => ({
      id: `t-${t.tool}`,
      icon: IconTool,
      label: t.tool ?? "",
      sub: `Tool · ${kindLabel(t.kind)}`,
      text: `${t.tool} tool ${t.kind}`,
      run: go(toolPath(t.tool ?? "")),
    }));
    const navs: Item[] = sections.flatMap((s) => [
      { id: `n-${s.key}`, icon: IconArrowRight, label: s.label, sub: "Go to", run: go(s.to) },
      ...s.subs
        .filter((c) => c.key !== "current")
        .map((c) => ({
          id: `n-${s.key}-${c.key}`,
          icon: IconArrowRight,
          label: `${s.label} › ${c.label}`,
          sub: "Go to",
          run: go(c.to),
        })),
    ]);
    return [...flows, ...emus, ...navs, ...toolItems, ...snapItems, ...people];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, emulators, snaps, members, tenantTools, session, colorScheme, toolDistribution]);

  const needle = q.trim().toLowerCase();
  const results = needle
    ? items.filter((i) => (i.text ?? i.label).toLowerCase().includes(needle) || i.sub.toLowerCase().includes(needle)).slice(0, 40)
    : items.filter((i) => !i.id.startsWith("s-") && !i.id.startsWith("m-") && !i.id.startsWith("t-")).slice(0, 14);

  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${sel}"]`)?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  return (
    <div>
      <div className={classes.palInput}>
        <IconSearch size={18} style={{ color: "var(--na-fg-muted)" }} />
        <input
          data-autofocus
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Emulators, snapshots, members, actions"
          aria-label="Search"
          role="combobox"
          aria-expanded
          aria-controls="na-palette-list"
          aria-activedescendant={results[sel] ? `pal-${results[sel].id}` : undefined}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setSel((s) => Math.min(results.length - 1, s + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setSel((s) => Math.max(0, s - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              results[sel]?.run();
            }
          }}
        />
        <Kbd>esc</Kbd>
      </div>
      <div id="na-palette-list" role="listbox" className={classes.palList} ref={listRef}>
        {results.map((r, i) => (
          <button
            key={r.id}
            id={`pal-${r.id}`}
            type="button"
            role="option"
            aria-selected={i === sel}
            data-index={i}
            data-selected={i === sel || undefined}
            className={classes.palItem}
            onMouseMove={() => setSel(i)}
            onClick={r.run}
          >
            <r.icon size={16} />
            <span className={classes.palLabel}>{r.label}</span>
            <span className={classes.palSub}>{r.sub}</span>
          </button>
        ))}
        {results.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: "var(--na-fg-muted)" }}>No matches</div>
        )}
      </div>
    </div>
  );
}
